import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { PlanHeader } from '@/types/finance/PlanHeader';
import { authService } from '@/services/auth/authService';

/**
 * A PlanHeader as served to the Plan Entry surface: the header plus the caller's computed edit
 * right (false for subtree/ViewAll oversight viewers and for SUBMITTED headers). Mirrors the
 * backend PlanEntryHeaderDto. spec: docs/15-designspecs/Plan-Entry-Assignment-Access.md.
 */
export interface PlanEntryHeader extends PlanHeader {
  canEdit: boolean;
  /** PlanVersion -> PlanScenario.Name, so the selector can show and group by it without reading
   *  the PlanVersion list (a permission the contributor role does not hold). */
  planScenarioName?: string | null;
  /** The version's sequence number, for the "v{n}" fallback when Label is empty. */
  planVersionNo?: number | null;
}

interface PagedResult<T> {
  totalCount: number;
  items: T[];
}

export const myEntryHeadersKey = (tenantId?: string | null) =>
  ['my-entry-headers', tenantId] as const;

/**
 * The assignment-filtered Plan Entry header list — only headers the current user may enter
 * (AssignedTo/Owner principal match) or oversee (ViewSubtree/ViewAll), filtered server-side.
 * Replaces the unfiltered PlanHeader getAll() on the Plan Entry surface.
 */
export function useMyEntryHeaders() {
  const tenantId = authService.getTenantId();

  return useQuery<PlanEntryHeader[], Error>({
    queryKey: myEntryHeadersKey(tenantId),
    queryFn: async () => {
      const response = await apiClient.get<PagedResult<PlanEntryHeader>>(
        '/api/finance/planheader/my-entry-headers',
        { params: { maxResultCount: 1000 } },
      );
      return response.data.items;
    },
  });
}
